import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';

import authApi from '../services/authApi';
import { registerScouterLegacy } from '../services/legacyMike';
import {
  TOKEN_REFRESHED_EVENT,
  TokenRefreshedDetail,
} from '../services/authInterceptors';

interface AuthState {
  accessToken: string;
  refreshToken: string;
}

interface SignInCredentials {
  username: string;
  password: string;
}

interface AuthContextData {
  token: string;
  refreshToken: string;
  signIn(credeitnals: SignInCredentials): Promise<void>;
  signOut(): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const accessToken = localStorage.getItem('@Scout:accessToken');
    const refreshToken = localStorage.getItem('@Scout:refreshToken');

    if (accessToken && refreshToken) {
      return { accessToken, refreshToken };
    }

    return {} as AuthState;
  });

  // O interceptor renova o token fora do React; sem propagar para cá, o
  // socket continuaria autenticado com o token antigo até um reload.
  useEffect(() => {
    const handleRefresh = (event: Event) => {
      const { accessToken, refreshToken } = (event as CustomEvent<
        TokenRefreshedDetail
      >).detail;
      setData({ accessToken, refreshToken });
    };

    window.addEventListener(TOKEN_REFRESHED_EVENT, handleRefresh);
    return () => window.removeEventListener(TOKEN_REFRESHED_EVENT, handleRefresh);
  }, []);

  const signIn = useCallback(async ({ username, password }) => {
    try {
      const response = await authApi.post('/auth/login', {
        username,
        password,
      });

      const { accessToken, refreshToken } = response.data;

      localStorage.setItem('@Scout:accessToken', accessToken);
      localStorage.setItem('@Scout:refreshToken', refreshToken);

      setData({ accessToken, refreshToken });

      // Best-effort: a v1 está em desativação e não pode bloquear o login.
      await registerScouterLegacy(username, accessToken);
    } catch (error) {
      throw new Error('Credenciais inválidas');
    }
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@Scout:accessToken');
    localStorage.removeItem('@Scout:refreshToken');
    
    setData({} as AuthState);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token: data.accessToken,
        refreshToken: data.refreshToken,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { useAuth, AuthProvider };
